import { createBigDecimal } from '@0xtorch/big-decimal'
import type { Action, Transfer } from '@0xtorch/core'
import {
  fromIsRelated,
  haveTokenTransfers,
  isFeePayer,
  isRelatedTransfer,
} from '../../conditions'
import { nativeTokenDecimals } from '../../constants'
import { createAction, createTokenTransfer } from '../../generators'
import type { SolanaConverter } from '../../type'

/**
 * 61DFfeTKM7trxYcPQCM78bJ794ddZprZpAwAnLiwTpYH の Fill 命令
 * - Jupiter DEX経由でのトークンスワップ取引
 * - ログに "Instruction: Fill" が出力される
 *
 * **生成する Action**
 * - crypto 間の売買取引として trade action
 * - type = trade
 * - transfers
 *   - direction = in の SPL token transfers（受け取りトークン）
 *   - direction = out の SPL/Native token transfers（支払いトークン）
 *
 * **条件 (一致すべき pattern)**
 * - from
 *   - relatedAddress 内の address である
 * - transfers
 *   - token transfer が存在する
 *
 * **判定理由**
 * - programId が 61DFfeTKM7trxYcPQCM78bJ794ddZprZpAwAnLiwTpYH
 * - ログに "Instruction: Fill" が含まれており、Jupiter DEXのFill操作である
 * - トランザクションの実行者が relatedAddress である必要があるので、from の条件を設定
 * - Jupiter DEXを通じたスワップなので、token transfers が必須
 */
const convertToActions: SolanaConverter = (args) => {
  const { transaction, source, relatedAddresses } = args

  // 送信者が関連アドレスかチェック
  if (!fromIsRelated(args)) return undefined

  // トークン転送が含まれているかチェック
  if (!haveTokenTransfers(transaction)) return undefined

  // tokenTransfersFromInstructionsから関連アドレスの転送を抽出
  const actions: Action[] = []

  const swapTransfers: Transfer[] = []
  const createAccountTransfers: Transfer[] = []
  if (transaction.tokenTransfersFromInstructions) {
    for (const tokenTransfer of transaction.tokenTransfersFromInstructions) {
      if (!isRelatedTransfer(tokenTransfer, relatedAddresses)) {
        continue
      }

      const transfer = createTokenTransfer({
        tokenTransfer,
        relatedAddresses,
      })

      if (transfer) {
        if (tokenTransfer.instructionType === 'createAccount') {
          createAccountTransfers.push(transfer)
        } else {
          swapTransfers.push(transfer)
        }
      }
    }
  }

  // trade Actionを作成
  if (swapTransfers.length >= 2) {
    const action = createAction({
      order: actions.length,
      type: 'trade',
      transfers: swapTransfers,
      transaction,
      source,
    })
    actions.push(action)
  }

  // createAccount Actionを作成
  if (createAccountTransfers.length >= 1) {
    const action = createAction({
      order: actions.length,
      type: 'fee',
      transfers: createAccountTransfers,
      transaction,
      source,
    })
    actions.push(action)
  }

  // トランザクション手数料をout transferとして追加
  if (isFeePayer(args) && transaction.meta?.fee) {
    const feeAction = createAction({
      order: actions.length,
      type: 'transaction-fee',
      transfers: [
        {
          direction: 'out',
          asset: {
            symbol: 'SOL',
            type: 'CryptoCurrency',
            id: 'solana',
            name: 'Solana',
            updatedAt: 0,
          },
          amount: createBigDecimal(
            BigInt(transaction.meta.fee),
            nativeTokenDecimals,
          ),
        },
      ],
      transaction,
      source,
    })

    actions.push(feeAction)
  }

  return actions
}

export default convertToActions
